import React, { useState } from 'react';
import { Phone, Lock, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify'; // Import toast

// API base URL - replace with your actual environment variable or config
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; 

const ForgotPassword = () => {
    const [step, setStep] = useState(1);
    const [mobileNumber, setMobileNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetToken, setResetToken] = useState(null); 
    const [isLoading, setIsLoading] = useState(false); 
    const navigate = useNavigate();

  const handleNextStep = async (e) => {
  e.preventDefault();
  setIsLoading(true);

  try {
    /* ================= STEP 1: SEND OTP ================= */
    if (step === 1 && mobileNumber) {
      await axios.post(
        `${API_BASE_URL}api/auth/forgot-password/send-otp`,
        { mobileNumber }
      );

      toast.success(`OTP sent to ${mobileNumber}`);
      setStep(2);
    }

    /* ================= STEP 2: VERIFY OTP ================= */
    else if (step === 2 && otp) {
      const response = await axios.post(
        `${API_BASE_URL}api/auth/forgot-password/verify-otp`,
        {
          mobileNumber,
          otp,
        }
      );

      // 🔐 Save reset token
      setResetToken(response.data.resetToken);

      toast.success(response.data.message);
      setStep(3);
    }

    /* ================= STEP 3: RESET PASSWORD ================= */
    else if (step === 3) {
      if (newPassword !== confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }

      if (!resetToken) {
        toast.error("Reset token missing. Please restart.");
        return;
      }

      await axios.post(
        `${API_BASE_URL}api/auth/forgot-password/reset`,
        { newPassword },
        {
          headers: {
            Authorization: `Bearer ${resetToken}`,
          },
        }
      );

      toast.success("Password updated successfully");
      navigate("/login");
    }
  } catch (error) {
    toast.error(
      error.response?.data?.message || "Something went wrong"
    );
  } finally {
    setIsLoading(false);
  }
};


    const renderFormContent = () => {
        switch (step) {
            case 1:
                return (
                    <>
                        <div className="step-header">
                            <h1 className="step-title">Forgot Password?</h1>
                            <p className="step-subtitle">Enter your registered mobile number to receive a verification code.</p>
                        </div>
                        <form onSubmit={handleNextStep}>
                            <div className="form-group">
                                <div className="input-container">
                                    <span className="input-icon">
                                        <Phone size={20} />
                                    </span>
                                    <input
                                        type="tel"
                                        className="form-input"
                                        placeholder="Mobile Number"
                                        value={mobileNumber}
                                        onChange={(e) => setMobileNumber(e.target.value)}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <button type="submit" className="primary-button" disabled={isLoading}>
                                {isLoading ? 'Sending...' : 'Send OTP'}
                            </button>
                        </form>
                    </>
                );
            case 2:
                return (
                    <>
                        <div className="step-header">
                            <h1 className="step-title">Verify OTP</h1>
                            <p className="step-subtitle">A 6-digit code has been sent to {mobileNumber}.</p>
                        </div>
                        <form onSubmit={handleNextStep}>
                            <div className="form-group">
                                <div className="input-container">
                                    <span className="input-icon">
                                        <Lock size={20} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-input otp-input"
                                        placeholder="Enter 6-digit OTP"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        maxLength="6"
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <div className="button-group">
                                <button type="button" className="secondary-button" onClick={() => setStep(1)} disabled={isLoading}>
                                    Back
                                </button>
                                <button type="submit" className="forgot-button" disabled={isLoading}>
                                    {isLoading ? 'Verifying...' : 'Verify & Proceed'}
                                </button>
                            </div>
                        </form>
                    </>
                );
            case 3:
                return (
                    <>
                        <div className="step-header">
                            <h1 className="step-title">Set New Password</h1>
                            <p className="step-subtitle">Create a strong, new password for your account.</p>
                        </div>
                        <form onSubmit={handleNextStep}>
                            <div className="form-group">
                                <div className="input-container">
                                    <span className="input-icon">
                                        <Lock size={20} />
                                    </span>
                                    <input
                                        type="password"
                                        className="form-input"
                                        placeholder="New Password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <div className="input-container">
                                    <span className="input-icon">
                                        <Lock size={20} />
                                    </span>
                                    <input
                                        type="password"
                                        className="form-input"
                                        placeholder="Confirm New Password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <button type="submit" className="primary-button" disabled={isLoading}>
                                {isLoading ? 'Updating...' : 'Update Password'}
                            </button>
                        </form>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="register-container">
            {/* The style block remains here if you need to keep embedded CSS */}
            <style>{`
            `}</style>
            <div className="register-card">
                {/* Back button */}
                <button className="back-button" type="button" onClick={() => navigate(-1)} disabled={isLoading}>
                    ←
                </button>

                {/* Progress bar (no changes needed here) */}
                <div className="progress-container">
                    <div className="progress-item">
                        <div className={`progress-circle ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                            {step > 1 ? <Check size={20} /> : '1'}
                        </div>
                        <span className={`progress-label ${step === 1 ? 'active' : ''}`}>Mobile</span>
                        <div className={`progress-line ${step > 1 ? 'completed' : ''}`}></div>
                    </div>

                    <div className="progress-item">
                        <div className={`progress-circle ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                            {step > 2 ? <Check size={20} /> : '2'}
                        </div>
                        <span className={`progress-label ${step === 2 ? 'active' : ''}`}>Verification</span>
                        <div className={`progress-line ${step > 2 ? 'completed' : ''}`}></div>
                    </div>

                    <div className="progress-item">
                        <div className={`progress-circle ${step === 3 ? 'active' : ''}`}>3</div>
                        <span className={`progress-label ${step === 3 ? 'active' : ''}`}>Password</span>
                    </div>
                </div>

                {/* Dynamic form content based on the current step */}
                <div className="form-content">
                    {renderFormContent()}
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;